import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import useFormStore from '../state/useFormStore';
import { formatCurrency, parseLocalDate } from '../utils/utils';
import { apiService } from '../utils/api';
import { bankOptions } from '../data/selectOptions';
import { Button } from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PaymentsIcon from '@mui/icons-material/Payments';
import InfoIcon from '@mui/icons-material/Info';

const Container = styled.div`
  min-height: 100vh;
  background-color: #ffffff;
  padding: 16px 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ContentWrapper = styled.div`
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
`;

const TitleSection = styled.div`
  text-align: center;
`;

const Title = styled.h1`
  margin: 20px 0 10px;
  font-size: 24px;
  line-height: 32px;
  font-weight: 600;
  color: #333;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0 0 10px;
  text-align: center;
`;

const MainCard = styled.div`
  background-color: #f8f9fa;
  border-radius: 12px;
  padding: 24px;
  margin-bottom: 16px;
  width: 100%;
`;

const Card = styled.div`
  background-color: #ffffff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const Amount = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: #000;
  margin-bottom: 8px;
  text-align: center;
`;

const InstallmentInfo = styled.div`
  font-size: 18px;
  color: #000;
  text-align: center;
  margin-bottom: 0;
`;

const InstallmentAmount = styled.span`
  font-weight: 600;
  color: #000;
`;

const SectionTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: #666;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CbuBox = styled.div`
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 1px;
  background: #f8f9fa;
  border-radius: 8px;
  padding: 12px;
  margin-bottom: 12px;
  word-break: break-all;
  border: 1px solid #e0e0e0;
  text-align: center;
`;

const InfoText = styled.div`
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const PaymentList = styled.ul`
  font-size: 13px;
  color: #444;
  margin: 8px 0 0 0;
  padding-left: 20px;
  list-style-type: none;

  li {
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 6px;

    &:before {
      content: "•";
      color: #95d0dd;
      font-weight: bold;
      display: inline-block;
      width: 1em;
      margin-left: -1em;
    }
  }
`;

const RatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 24px;
`;

const RateCard = styled.div`
  background-color: #ffffff;
  border-radius: 8px;
  padding: 12px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
`;

const RateLabel = styled.div`
  font-size: 12px;
  color: #666;
  margin-bottom: 4px;
`;

const RateValue = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #000;
`;

const ActionSection = styled.div`
  padding: 16px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const SkipMessage = styled.div`
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  border: 1px solid #e0e0e0;
  text-align: center;
`;

const SkipText = styled.p`
  font-size: 14px;
  color: #666;
  margin: 0;
  line-height: 1.5;
`;

interface OfferResumen {
  offerId: string;
  selectedAmount: number;
  selectedInstallments: number;
  selectedInstallmentAmount: number;
  cbu?: string;
  bankName?: string;
  firstDueDate?: string;
  rate?: number;
  tea?: number;
  calculatorResult?: any;
  offerRaw?: any;
}

// Convierte el array a un objeto para lookup rápido
const BANKS: Record<string, string> = bankOptions.reduce((acc, bank) => {
  acc[bank.value] = bank.label;
  return acc;
}, {} as Record<string, string>);

const ResumenPrestamo: React.FC = () => {
  const navigate = useNavigate();
  const { selectedOfferData, hash, requestUid } = useFormStore();
  const [resumen, setResumen] = useState<OfferResumen | null>(null);
  const [loading, setLoading] = useState(true);
  const calculatorInstance = useRef<any>(null);

  // Cargar la calculadora si no está disponible
  useEffect(() => {
    if (!window.PresterLoanCalculator && !document.getElementById('prester-loan-calculator')) {
      const script = document.createElement("script");
      script.id = 'prester-loan-calculator';
      script.src = "https://cirenio-staging.nyc3.cdn.digitaloceanspaces.com/prester-loan-calculator.min.js";
      script.async = false;
      script.onload = () => {
        if (window.PresterLoanCalculator) {
          calculatorInstance.current = new window.PresterLoanCalculator();
        }
      };
      document.body.appendChild(script);
    } else if (window.PresterLoanCalculator && !calculatorInstance.current) {
      calculatorInstance.current = new window.PresterLoanCalculator();
    }
  }, []);

  // Extraer la expresión compleja a una variable
  const requestUidValue = requestUid ?? undefined;

  useEffect(() => {
    const fetchResumen = async () => {
      let resumenObj: OfferResumen | null = null;

      try {
        // 1. Intentar obtener de localStorage
        const resumenLS = localStorage.getItem('selectedOfferResumen');
        if (resumenLS) {
          try {
            const parsed = JSON.parse(resumenLS);
            resumenObj = normalizeResumen(parsed, requestUidValue);
          } catch (e) {
            console.error('Error parsing localStorage data:', e);
          }
        }

        // 2. Si no está, obtener del endpoint y simular
        if (!resumenObj && requestUidValue) {
          const response = await apiService.requests.getOffers(requestUidValue);
          const data = response.data?.data;
          
          // Si viene en formato API con finalOffers
          if (data?.finalOffers && data?.requestedAmount) {
            const offer = data.finalOffers.find((o: any) => o.selected);
            if (offer && (window.PresterLoanCalculator || calculatorInstance.current)) {
              const calc = calculatorInstance.current || new window.PresterLoanCalculator();
              calc.setup({
                amount: Number(data.requestedAmount),
                installments: offer.installments,
                interest: offer.rate * 100,
              });
              const calculatorResult = calc.calculate();
              resumenObj = {
                offerId: offer.id,
                selectedAmount: Number(data.requestedAmount),
                selectedInstallments: offer.installments,
                selectedInstallmentAmount: calculatorResult?.installments?.[0]?.amount || offer.maxInstallmentAmount,
                cbu: '',
                bankName: '',
                firstDueDate: offer.firstDueDate,
                rate: offer.rate,
                tea: calculatorResult?.loan?.tea,
                calculatorResult,
                offerRaw: offer,
              };
              resumenObj = normalizeResumen(resumenObj, requestUidValue);
              localStorage.setItem('selectedOfferResumen', JSON.stringify(resumenObj));
            }
          } else {
            // Fallback: buscar por selectedOfferId como antes
            const offerId = selectedOfferData?.offerId || localStorage.getItem('selectedOfferId');
            let offer;
            if (offerId) {
              offer = response.data?.data?.offers?.find((o: any) => o.id === offerId);
            } else {
              offer = response.data?.data?.offers?.[0];
            }
            if (offer) {
              resumenObj = normalizeResumen(offer, requestUidValue);
              localStorage.setItem('selectedOfferResumen', JSON.stringify(resumenObj));
            }
          }
        }
      } catch (error) {
        console.error('Error fetching resumen:', error);
      } finally {
        setResumen(resumenObj || null);
        setLoading(false);
      }
    };

    // Esperar a que la calculadora esté lista antes de simular
    if (window.PresterLoanCalculator || calculatorInstance.current) {
      fetchResumen();
    } else {
      const interval = setInterval(() => {
        if (window.PresterLoanCalculator || calculatorInstance.current) {
          clearInterval(interval);
          fetchResumen();
        }
      }, 100);
      return () => clearInterval(interval);
    }
  }, [requestUidValue, selectedOfferData]);

  // Función para normalizar el objeto resumen
  function normalizeResumen(data: any, requestUid?: string): OfferResumen {
    const offerRaw = data.offerRaw || data;
    const calculatorResult = data.calculatorResult || null;

    // Obtener CBU o banco
    let cbu = data.cbu || offerRaw.cbu || '';
    let bankName = '';
    
    // Primero intentar obtener el CBU del localStorage
    const storedCBU = localStorage.getItem('userCBU');
    if (storedCBU) {
      cbu = storedCBU;
    }
    // Intentar obtener el banco aunque haya CBU
    if (requestUid) {
      const userDataStr = localStorage.getItem(`prestix_${requestUid}`);
      if (userDataStr) {
        try {
          const userData = JSON.parse(userDataStr);
          if (userData.bank && BANKS[userData.bank]) {
            bankName = BANKS[userData.bank];
          }
        } catch (e) {}
      }
    }

    return {
      offerId: data.offerId || offerRaw.id,
      selectedAmount: data.selectedAmount || offerRaw.selectedAmount || offerRaw.maxAmount,
      selectedInstallments: data.selectedInstallments || offerRaw.selectedInstallments || offerRaw.installments,
      selectedInstallmentAmount: data.selectedInstallmentAmount || offerRaw.selectedInstallmentAmount || offerRaw.maxInstallmentAmount,
      cbu,
      bankName,
      firstDueDate: data.firstDueDate || offerRaw.firstDueDate,
      rate: data.rate || offerRaw.rate,
      tea: data.tea || (calculatorResult?.loan?.tea ?? offerRaw.tea),
      calculatorResult,
      offerRaw,
    };
  }

  if (loading || !resumen) {
    return <div>Cargando resumen...</div>;
  }

  return (
    <Container>
      <ContentWrapper>
        <TitleSection>
          <Title>Resumen de tu préstamo</Title>
        </TitleSection>

        <MainCard>
          <Subtitle>Revisá los detalles antes de continuar</Subtitle>
          <Card>
            <Amount>{formatCurrency(resumen.selectedAmount)}</Amount>
            <InstallmentInfo>
              <InstallmentAmount>
                {resumen.selectedInstallments} cuotas de {formatCurrency(resumen.selectedInstallmentAmount)}
              </InstallmentAmount>
            </InstallmentInfo>
          </Card>

          <Card>
            <SectionTitle>
              <AccountBalanceIcon fontSize="small" />
              DATOS DE TRANSFERENCIA
            </SectionTitle>
            {resumen.cbu === "SKIPPED" ? (
              <SkipMessage>
                <SkipText>
                  Te solicitaremos tu CBU de {resumen.bankName} para realizar la transferencia de los fondos.
                  Un analista se pondrá en contacto contigo para completar este paso.
                </SkipText>
              </SkipMessage>
            ) : (
              <>
                <CbuBox>
                  {resumen.cbu ? resumen.cbu : 'No disponible'}
                </CbuBox>
                {resumen.bankName && (
                  <InfoText>
                    <InfoIcon fontSize="small" />
                    Banco: {resumen.bankName}
                  </InfoText>
                )}
              </>
            )}
          </Card>

          <Card>
            <SectionTitle>
              <CalendarTodayIcon fontSize="small" />
              FECHAS DE PAGO
            </SectionTitle>
            <InfoText>
              Tu cuota vence el {resumen.firstDueDate ? (parseLocalDate(resumen.firstDueDate)?.getDate() === 10 ? 5 : parseLocalDate(resumen.firstDueDate)?.getDate()) : '--'} de cada mes
            </InfoText>
          </Card>

          <Card>
            <SectionTitle>
              <PaymentsIcon fontSize="small" />
              FORMA DE PAGO
            </SectionTitle>
            <PaymentList>
              <li>Débito automático con tarjeta de débito</li>
              <li>Transferencia bancaria</li>
            </PaymentList>
          </Card>

          <RatesGrid>
            <RateCard>
              <RateLabel>TNA</RateLabel>
              <RateValue>
                {resumen.rate
                  ? (resumen.rate * 100).toFixed(2) + '%'
                  : resumen.calculatorResult?.loan?.tna_iva
                  ? resumen.calculatorResult.loan.tna_iva + '%'
                  : 'No disponible'}
              </RateValue>
            </RateCard>
            <RateCard>
              <RateLabel>TEA</RateLabel>
              <RateValue>
                {resumen.tea
                  ? resumen.tea + '%'
                  : resumen.calculatorResult?.loan?.tea
                  ? resumen.calculatorResult.loan.tea + '%'
                  : 'No disponible'}
              </RateValue>
            </RateCard>
            <RateCard>
              <RateLabel>CFT</RateLabel>
              <RateValue>
                {resumen.calculatorResult?.loan?.cft
                  ? resumen.calculatorResult.loan.cft + '%'
                  : 'No disponible'}
              </RateValue>
            </RateCard>
          </RatesGrid>

          <ActionSection>
            <Button
              variant="contained"
              onClick={() => navigate(`/${hash}/firma`)}
              disabled={loading}
              style={{
                backgroundColor: "#FFF200",
                color: "black",
                fontWeight: 600,
                borderRadius: 50,
                padding: "12px 60px",
                boxShadow: "none",
                textTransform: "none",
                fontSize: 15,
              }}
            >
              {loading ? "Cargando..." : "Continuar"}
            </Button>
          </ActionSection>
        </MainCard>
      </ContentWrapper>
    </Container>
  );
};

export default ResumenPrestamo;